import html2canvas from 'html2canvas';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMap } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { documentsActions } from 'app-components/flux/documents';
import { undoActions, undoTypes } from 'app-components/flux/undo';
import { crashReporterInterface, platform, tracker } from 'app-components/utils/core';
import { isDevBuild } from 'app-components/utils/core/config';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { useFetchOnce } from 'app-components/hooks/utils';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import { sendFeedback } from 'data/feedback/feedbackActions';
import { sanitizeData } from 'utils/axiosFactory';
import consoleMirror from 'utils/consoleMirror';
import { getNetworkLogs } from 'utils/networkLogs';
import { copyState } from 'utils/storeUtils';
import { v4 as uuidv4 } from 'uuid';

import ErrorIcon from '@mui/icons-material/Error';
import LoadingView from 'components/LoadingView';
import { makeStyles } from 'tss-react/mui';

export const DIALOG_TYPE_REPORT_ERROR = 'DIALOG_TYPE_REPORT_ERROR';

const useStyles = makeStyles()((theme) => ({
  errorDialog: {
    display: 'flex',
    marginTop: 20,
    alignItems: 'center',
  },
  errorButton: {
    marginRight: theme.spacing(1),
  },
}));

const clientType = 'Simplifi Web App';
const client = 'Simplifi';

const SendReportDialog = React.memo((props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dialogId, onClose, feedbackSubject, feedbackCategory, attachments, ...otherProps } = props;
  const { classes } = useStyles();
  const [screenShotPNG, setScreenShotPNG] = useState();
  const [screenShotBlob, setScreenShotBlob] = useState();

  useEffect(() => {
    html2canvas(document.body)
      .then((canvas) => {
        canvas.toBlob((blob) => setScreenShotBlob(blob), 'mage/png');
        setScreenShotPNG(canvas.toDataURL('image/png'));
      })
      .catch((error) => assert(false, error));
  }, []);

  const dispatch = useDispatch();
  const [sanitizeFile, setSanitizeFile] = useState(!isDevBuild());
  const [submitDisabled, setSubmitDisabled] = useState();
  const [message, setMessage] = useState('');
  const [errorState, setErrorState] = useState(false);

  const dataset = useSelector(authSelectors.getCurrentDataset);
  const fileName = `${dataset?.name || 'no-dataset'} [${DateTime.local().toISODate()}].json`;
  const consoleLogsName = 'console logs.txt';
  const networkLogsName = 'network logs.txt';
  const screenshotName = 'screenshot.png';
  const localStorageName = 'localStorage.json';
  const sessionStorageName = 'sessionStorage.json';

  const [selectedAttachments, { set: setSelectedAttachment }] = useMap({
    ...Object.fromEntries(Object.keys(attachments || {}).map((attachmentFileName) => [attachmentFileName, true])),
    [fileName]: true,
    [consoleLogsName]: true,
    [networkLogsName]: true,
    [screenshotName]: true,
    [localStorageName]: true,
    [sessionStorageName]: true,
  });

  const authSession = useSelector(authSelectors.getAuthSession);
  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors);
  const subscription = useSelector(subscriptionsSelectors.getActiveSubscription);
  const stdDialogRef = useRef();

  return (
    <StdDialog
      sharedcomponentid={'DIALOG_TYPE_REPORT_ERROR'}
      stdDialogRef={stdDialogRef}
      size='md'
      title='Send Feedback'
      {...otherProps}
    >
      <DialogContent>
        {!submitDisabled ? (
          <Box display='flex' flexDirection='column'>
            <Typography>
              {'Send us the issue you’re experiencing. We will not respond to this feedback directly. ' +
                'This information is used for statistical and investigative purposes only. ' +
                'Have questions? Try '}
              <Link id='intercom-launcher'>help or support</Link>.
            </Typography>
            <br />
            <TextField
              autoFocus
              id='message'
              label='Describe your issue'
              placeholder='Please describe the issue you are experiencing'
              multiline
              rows={4}
              maxRows={4}
              variant='filled'
              onChange={(event) => setMessage(event.currentTarget.value)}
              inputProps={{ maxLength: 255 }}
              helperText={`You can enter up to 255 characters (${255 - message.length} remaining).`}
            />
            {errorState && (
              <div className={classes.errorDialog}>
                <ErrorIcon color='error' fontSize='small' className={classes.errorButton} />
                <Typography> Oops. We are unable to upload your files. Please try again.</Typography>
              </div>
            )}
            <br />

            <Box display='flex'>
              <Box display='flex' flexDirection='column' m={2} minWidth={250} flexShrink={0}>
                <Typography color='textSecondary'>What to include</Typography>

                {Object.keys(attachments || {}).map((attachmentFileName) => (
                  <FormControlLabel
                    key={attachmentFileName}
                    value={attachmentFileName}
                    label={attachmentFileName}
                    control={<Checkbox color='primary' />}
                    labelPlacement='end'
                    checked={selectedAttachments[attachmentFileName]}
                    onChange={(event, value) => setSelectedAttachment(attachmentFileName, value)}
                  />
                ))}

                <FormControlLabel
                  value='local-storage'
                  control={<Checkbox color='primary' />}
                  label='Local Storage'
                  labelPlacement='end'
                  checked={selectedAttachments[localStorageName]}
                  onChange={(event, value) => setSelectedAttachment(localStorageName, value)}
                />

                <FormControlLabel
                  value='session-storage'
                  control={<Checkbox color='primary' />}
                  label='Session Storage'
                  labelPlacement='end'
                  checked={selectedAttachments[sessionStorageName]}
                  onChange={(event, value) => setSelectedAttachment(sessionStorageName, value)}
                />

                <FormControlLabel
                  value='console-logs'
                  control={<Checkbox color='primary' />}
                  label='Console Logs'
                  labelPlacement='end'
                  checked={selectedAttachments[consoleLogsName]}
                  onChange={(event, value) => setSelectedAttachment(consoleLogsName, value)}
                />

                <FormControlLabel
                  value='network-logs'
                  control={<Checkbox color='primary' />}
                  label='Network logs'
                  labelPlacement='end'
                  checked={selectedAttachments[networkLogsName]}
                  onChange={(event, value) => setSelectedAttachment(networkLogsName, value)}
                />

                <FormControlLabel
                  value='file'
                  control={<Checkbox color='primary' />}
                  label={
                    <>
                      {client} file&nbsp;
                      <Tooltip
                        arrow
                        placement='right'
                        title={
                          <>
                            <p>
                              Your local {client} file could include all your personal data including payees, accounts
                              names, transactions, etc.
                            </p>
                            <p>
                              <b>Send as is:</b>
                              <br />
                              We will receive your file with nothing hidden.
                            </p>
                            <p>
                              <b>Sanitize before sending:</b>
                              <br />
                              All your personal data will be hidden/un-readable before it is sent to us.
                            </p>
                          </>
                        }
                      >
                        <InfoOutlinedIcon fontSize='small' />
                      </Tooltip>
                    </>
                  }
                  labelPlacement='end'
                  checked={selectedAttachments[fileName]}
                  onChange={(event, value) => setSelectedAttachment(fileName, value)}
                />
                <Collapse in={selectedAttachments[fileName]} mountOnEnter unmountOnExit>
                  <Box paddingLeft={4}>
                    <RadioGroup
                      aria-label='sanitize'
                      name='sanitize'
                      value={sanitizeFile ? 'sanitize' : 'as-is'}
                      onChange={(event, value) => setSanitizeFile(value === 'sanitize')}
                    >
                      <FormControlLabel
                        disabled={!selectedAttachments[fileName]}
                        value='as-is'
                        control={<Radio color='primary' />}
                        label='Send as is'
                      />
                      <FormControlLabel
                        disabled={!selectedAttachments[fileName]}
                        value='sanitize'
                        control={<Radio color='primary' />}
                        label='Sanitize before sending'
                      />
                    </RadioGroup>
                  </Box>
                </Collapse>
              </Box>

              <Box display='flex' flexDirection='column' p={2}>
                <Typography color='textSecondary'>Screenshots</Typography>
                <FormControlLabel
                  value='screenshot'
                  control={<Checkbox color='primary' />}
                  label='Include Screenshot'
                  labelPlacement='end'
                  checked={selectedAttachments[screenshotName]}
                  onChange={(event, value) => setSelectedAttachment(screenshotName, value)}
                />
                <Collapse in={selectedAttachments[screenshotName]} mountOnEnter unmountOnExit>
                  <Paper variant='outlined'>
                    <img alt='Capturing screenshot...' height={240} src={screenShotPNG} />
                  </Paper>
                </Collapse>
              </Box>
            </Box>
          </Box>
        ) : (
          <LoadingView size={200} />
        )}
      </DialogContent>

      <DialogActions>
        {!submitDisabled && (
          <QButton variant='outlined' onClick={() => stdDialogRef.current?.closeDialog?.()}>
            Cancel
          </QButton>
        )}

        <QButton
          id='submit-button'
          variant='contained'
          disabled={submitDisabled}
          onClick={() => {
            setSubmitDisabled(true);
            new Promise((resolve, reject) => {
              dispatch(
                sendFeedback(
                  {
                    subject: feedbackSubject,
                    message,
                    ownerId: authSession?.qcsId,
                    category: feedbackCategory || feedbackSubject,
                    clientType,
                    os: `${platform.os.name} ${platform.os.version}`,
                    buildNumber: `${process.env.APP_VERSION} (${process.env.BUILD_NUMBER})`,
                    deviceType: platform.device.type,
                    tier: subscription?.tierName,
                    usage: window?.location?.href?.substr(0, 100), // usage 100 chars max
                    attachments: [
                      ...(!selectedAttachments[localStorageName]
                        ? []
                        : [
                            {
                              mimeType: 'application/json',
                              name: localStorageName,
                            },
                          ]),
                      ...(!selectedAttachments[sessionStorageName]
                        ? []
                        : [
                            {
                              mimeType: 'application/json',
                              name: sessionStorageName,
                            },
                          ]),
                      ...(!selectedAttachments[fileName]
                        ? []
                        : [
                            {
                              mimeType: 'application/json',
                              name: fileName,
                            },
                          ]),
                      ...(!selectedAttachments[consoleLogsName]
                        ? []
                        : [
                            {
                              mimeType: 'text/plain',
                              name: consoleLogsName,
                            },
                          ]),
                      ...(!selectedAttachments[networkLogsName]
                        ? []
                        : [
                            {
                              mimeType: 'text/plain',
                              name: networkLogsName,
                            },
                          ]),
                      ...(!selectedAttachments[screenshotName]
                        ? []
                        : [
                            {
                              mimeType: 'image/png',
                              name: screenshotName,
                            },
                          ]),
                      ...Object.entries(attachments || {})
                        .filter(([attachmentFileName]) => selectedAttachments[attachmentFileName])
                        .map(([attachmentFileName, blob]) => ({
                          mimeType: blob.type,
                          name: attachmentFileName,
                        })),
                    ],
                  },
                  {
                    resolve,
                    reject,
                    axiosConfig: {
                      headers: {
                        ...(authSession?.datasetId && { 'qcs-dataset-id': authSession?.datasetId }),
                      },
                    },
                  },
                ),
              );
            })
              .then((feedbackResult) => {
                // feedback UI is not usable, so send a copy to mixpanel and bugsnag
                tracker.track(tracker.events.feedback, {
                  feedbackId: feedbackResult?.data?.id,
                  subject: feedbackSubject,
                  message,
                  category: feedbackCategory,
                  usage: window?.location?.href?.substr(0, 100), // usage 100 chars max
                });

                const feedbackError = new Error(message);
                feedbackError.name = 'Feedback';
                crashReporterInterface.reportError(
                  feedbackError,
                  {
                    custom: {
                      feedbackId: feedbackResult?.data?.id,
                      subject: feedbackSubject,
                      message,
                      category: feedbackCategory,
                    },
                  },
                  {
                    context: feedbackSubject,
                  },
                );
                const promiseArray = [];

                if (selectedAttachments[localStorageName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === localStorageName,
                  );
                  const data = JSON.stringify(localStorage, null, 2);
                  if (assert(attachment?.uploadUri && data, 'can not upload local storage file attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'application/json',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                if (selectedAttachments[sessionStorageName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === sessionStorageName,
                  );
                  const dataObj = Object.fromEntries(
                    Object.entries(sessionStorage).map(([key, value]) => {
                      let parsedValue = value;
                      try {
                        parsedValue = JSON.parse(value);
                      } catch (_error) {
                        /* do nothing */
                      }
                      return [key, parsedValue];
                    }),
                  );
                  if (dataObj.auth_session?.accessToken) {
                    dataObj.auth_session.accessToken = '***';
                  }
                  if (dataObj.auth_session?.refreshToken) {
                    dataObj.auth_session.refreshToken = '***';
                  }
                  const data = JSON.stringify(dataObj, null, 2);
                  if (assert(attachment?.uploadUri && data, 'can not upload session storage file attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'application/json',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                if (selectedAttachments[fileName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === fileName,
                  );
                  const data = copyState(undefined, sanitizeFile);
                  if (assert(attachment?.uploadUri && data, 'can not upload feedback file attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'application/json',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                if (selectedAttachments[consoleLogsName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === consoleLogsName,
                  );
                  const consoleLogs = consoleMirror.log.join('\n');
                  const data = new Blob([consoleLogs], { type: 'text/plain' });
                  if (assert(attachment?.uploadUri && data, 'can not upload feedback file attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'text/plain',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                if (selectedAttachments[screenshotName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === screenshotName,
                  );
                  const data = screenShotBlob;
                  if (assert(attachment?.uploadUri && data, 'can not upload feedback screenshot attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'image/png',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                if (selectedAttachments[networkLogsName]) {
                  const attachment = feedbackResult?.data?.attachments?.find(
                    (anAttachment) => anAttachment.name === networkLogsName,
                  );
                  const networkLogs = getNetworkLogs();
                  const networkLogsString = JSON.stringify(networkLogs, null, 2);
                  const networkLogsStringSanitized = sanitizeData(networkLogsString);
                  const data = new Blob([networkLogsStringSanitized], { type: 'text/plain' });
                  if (assert(attachment?.uploadUri && data, 'can not upload feedback network logs attachment')) {
                    promiseArray.push(
                      new Promise((resolve, reject) =>
                        dispatch(
                          documentsActions.uploadData(
                            {
                              url: attachment.uploadUri,
                              data,
                              contentType: data.type || 'text/plain',
                            },
                            { resolve, reject },
                          ),
                        ),
                      ),
                    );
                  }
                }

                Object.entries(attachments || {})
                  .filter(([attachmentFileName]) => selectedAttachments[attachmentFileName])
                  .forEach(([attachmentFileName, data]) => {
                    const attachment = feedbackResult?.data?.attachments?.find(
                      (anAttachment) => anAttachment.name === attachmentFileName,
                    );
                    if (assert(attachment?.uploadUri && data, 'can not upload feedback network logs attachment')) {
                      promiseArray.push(
                        new Promise((resolve, reject) =>
                          dispatch(
                            documentsActions.uploadData(
                              {
                                url: attachment.uploadUri,
                                data,
                                contentType: data.type || 'text/plain',
                              },
                              { resolve, reject },
                            ),
                          ),
                        ),
                      );
                    }
                  });
                Promise.all(promiseArray)
                  .then(() => {
                    stdDialogRef.current?.closeDialog?.();
                    dispatch(
                      undoActions.addUndo(
                        undoTypes.mkUndo({
                          userMessage: `report created - reference id ${feedbackResult?.data?.id}`,
                          key: uuidv4(),
                        }),
                      ),
                    );
                  })
                  .catch((error) => {
                    setErrorState(true);
                    setSubmitDisabled(false);
                    assert(false, error);
                  });
              })
              .catch((error) => assert(false, error));
          }}
        >
          Send
        </QButton>
      </DialogActions>
    </StdDialog>
  );
});

SendReportDialog.propTypes = {
  transaction: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool, // set manually for controlled dialog
  dialogId: PropTypes.string, // set automatically for uncontrolled dialog
  feedbackSubject: PropTypes.string,
  feedbackCategory: PropTypes.string,
  attachments: PropTypes.object,
};

SendReportDialog.whyDidYouRender = true;

SendReportDialog.displayName = 'SendReportDialog';

export default SendReportDialog;
